(() => {
  const COOKIE = "provoque_provider";
  const EVENT = "provoque:provider-changed";

  const readCookie = () => {
    const match = document.cookie.split("; ").find((part) => part.startsWith(`${COOKIE}=`));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match.slice(COOKIE.length + 1)));
    } catch (error) {
      return null;
    }
  };

  const writeCookie = (value) => {
    document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(value))}; path=/; SameSite=Lax`;
  };

  const cartRoot = () => window.Shopify?.routes?.root || "/";
  const NOTE_PREFIX = "Provoque provider:";

  const stripProviderNote = (existing) =>
    String(existing || "")
      .replace(new RegExp(`(^|\\n)${NOTE_PREFIX}.*`, "g"), "")
      .trim();

  const cartAttributes = (payload) => ({
    provoque_has_provider: payload.choice || "",
    "Provoque provider": payload.name || (payload.choice === "no" ? "Dr. Ellis" : ""),

    "Provoque provider account ID": payload.accountId || "",
    "Provoque provider requested": payload.requested || "",
    provoque_provider_handle: payload.handle || ""
  });

  const pushCart = async (payload) => {
    if (!payload || (payload.choice !== "yes" && payload.choice !== "not_listed" && payload.choice !== "no")) {
      return null;
    }
    if (payload.choice === "yes" && !payload.name) {
      return null;
    }
    if (payload.choice === "not_listed" && !payload.requested) {
      return null;
    }

    let existingNote = "";
    try {
      const current = await fetch(`${cartRoot()}cart.js`, { headers: { Accept: "application/json" } }).then((response) => response.json());
      existingNote = current.note || "";
    } catch (error) {}
    const note = stripProviderNote(existingNote);
    const body = { attributes: cartAttributes(payload) };
    if (note !== String(existingNote || "").trim()) {
      body.note = note;
    }
    const response = await fetch(`${cartRoot()}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    });
    if (response.ok) {
      if (Object.prototype.hasOwnProperty.call(body, "note")) {
        document.querySelectorAll('textarea[name="note"], input[name="note"]').forEach((field) => {
          field.value = note;
        });
      }

      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
    return response;
  };

  const mapSrc = (query) => {
    const q = encodeURIComponent(query || "United States");
    return `https://maps.google.com/maps?q=${q}&z=11&output=embed`;
  };

  const providerQuery = (provider) =>
    [provider.address, provider.city, provider.state, provider.zip, provider.name].filter(Boolean).join(", ");

  const haystack = (provider) =>
    [provider.name, provider.city, provider.state, provider.zip, provider.address, provider.phone, provider.accountId, provider.handle]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const matchesQuery = (provider, query) => {
    const hay = haystack(provider);
    if (hay.includes(query)) return true;
    return query.split(/[\s,]+/).filter(Boolean).every((token) => hay.includes(token));
  };

  const toRad = (value) => (Number(value) * Math.PI) / 180;

  const distanceKm = (from, provider) => {
    const lat = Number(provider.lat);
    const lng = Number(provider.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return Number.POSITIVE_INFINITY;
    const dLat = toRad(lat - from.lat);
    const dLng = toRad(lng - from.lng);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(from.lat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.sqrt(a));
  };

  const paintHeader = (payload) => {
    document.querySelectorAll("[data-provider-label]").forEach((el) => {
      const fallback = el.getAttribute("data-fallback-label") || el.textContent;
      if (payload && payload.choice === "yes" && payload.name) {
        el.textContent = payload.name;
      } else if (payload && payload.choice === "not_listed" && payload.requested) {
        el.textContent = payload.requested;
      } else {
        el.textContent = fallback;
      }
    });
    document.querySelectorAll("[data-provider-cart-label]").forEach((el) => {
      const nameEl = el.querySelector("[data-provider-cart-name]");
      const text = payload && (payload.name || payload.requested);
      if (text) {
        el.hidden = false;
        if (nameEl) nameEl.textContent = payload.name || payload.requested;
      } else {
        el.hidden = true;
      }
    });
    document.querySelectorAll("[data-has-provider-input]").forEach((el) => {
      el.value = payload?.choice || "";
    });
    document.querySelectorAll("[data-provider-attribution-input]").forEach((el) => {
      el.value = payload?.name || "";
    });
    document.querySelectorAll("[data-provider-account-id-input]").forEach((el) => {
      el.value = payload?.accountId || "";
    });
    document.querySelectorAll("[data-provider-requested-input]").forEach((el) => {
      el.value = payload?.requested || "";
    });
    document.querySelectorAll("[data-provider-handle-input]").forEach((el) => {
      el.value = payload?.choice === "yes" ? payload.handle || "" : "";
    });
  };

  class ProviderSearchModal extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === "true") return;
      this.dataset.bound = "true";
      this.providers = [];
      try {
        this.providers = JSON.parse(this.querySelector("[data-providers-json]")?.textContent || "[]");
      } catch (error) {
        this.providers = [];
      }
      this.filtered = [];
      this.hasSearched = false;
      this.selectedHandle = "";
      this.mode = "search";

      this.modal = this.querySelector("x-modal");
      this.form = this.querySelector("[data-provider-search-form]");
      this.input = this.querySelector("[data-provider-search-input]");
      this.list = this.querySelector("[data-provider-results]");
      this.empty = this.querySelector("[data-provider-empty]");
      this.map = this.querySelector("[data-provider-map]");
      this.locate = this.querySelector("[data-provider-locate]");
      this.consent = this.querySelector("[data-provider-consent]");
      this.save = this.querySelector("[data-provider-save]");
      this.error = this.querySelector("[data-provider-error]");
      this.requestForm = this.querySelector(".provider-search__request");
      this.requestPanel = this.querySelector("[data-provider-request-panel]");
      this.requestInput = this.querySelector("[data-provider-request-input]");
      this.requestEmail = this.querySelector("[data-provider-request-email]");
      this.requestBody = this.querySelector("[data-provider-request-body]");
      this.requestSubmit = this.querySelector("[data-provider-request-submit]");
      this.requestSuccess = this.querySelector("[data-provider-request-success]");
      this.errorSelect = this.getAttribute("data-error-select") || "";
      this.errorConsent = this.getAttribute("data-error-consent") || "";
      this.requestError = this.getAttribute("data-request-error") || "";

      this.form?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.search(this.modal?.querySelector("[data-provider-search-input]")?.value || "");
      });
      this.modal?.addEventListener("submit", (event) => {
        if (!event.target.closest("[data-provider-search-form]")) return;
        event.preventDefault();
        this.search(this.modal.querySelector("[data-provider-search-input]")?.value || "");
      });
      this.modal?.addEventListener("input", (event) => {
        if (!event.target.closest("[data-provider-search-input]")) return;
        this.search(event.target.value || "");
      });
      this.modal?.addEventListener("search", (event) => {
        if (!event.target.closest("[data-provider-search-input]")) return;
        this.search(event.target.value || "");
      });
      this.locate?.addEventListener("click", () => this.useLocation());
      this.consent?.addEventListener("change", () => this.syncSave());
      this.modal?.addEventListener("click", (event) => {
        if (!event.target.closest("[data-provider-save]")) return;
        if (event.target.closest("[data-provider-save]")?.disabled) return;
        event.preventDefault();
        this.saveSelection();
      });
      this.requestForm?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.sendProviderRequest();
      });
      this.requestInput?.addEventListener("input", () => this.syncSave());
      this.list?.addEventListener("change", (event) => {
        const target = event.target.closest("[data-provider-handle]");
        if (!target) return;
        this.mode = "search";
        this.selectedHandle = target.getAttribute("data-provider-handle") || "";
        const provider = this.providers.find((item) => item.handle === this.selectedHandle);
        if (provider && this.map) this.map.src = mapSrc(providerQuery(provider));
        this.syncSave();
      });

      const saved = readCookie();
      if (saved) {
        this.selectedHandle = saved.handle || "";
        if (saved.choice === "not_listed") {
          this.mode = "request";
          if (this.requestInput) this.requestInput.value = saved.requested || "";
          this.requestPanel?.setAttribute("open", "");
        }
        paintHeader(saved);
      }
      this.renderList();
      this.syncSave();
      if (this.map) {
        const provider = this.providers.find((item) => item.handle === this.selectedHandle) || this.providers[0];
        this.map.src = mapSrc(provider ? providerQuery(provider) : "United States");
      }
      document.addEventListener(EVENT, (event) => paintHeader(event.detail));
    }

    search(raw) {
      const query = raw.trim().toLowerCase();
      this.mode = "search";
      this.hasSearched = Boolean(query);
      this.filtered = query ? this.providers.filter((provider) => matchesQuery(provider, query)) : [];
      if (this.map) {
        const first = this.filtered[0];
        this.map.src = mapSrc(first ? providerQuery(first) : raw.trim() || "United States");
      }
      this.renderList();
      this.syncSave();
    }

    async useLocation() {
      if (!navigator.geolocation || !this.locate) return;
      this.locate.disabled = true;
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const origin = { lat: position.coords.latitude, lng: position.coords.longitude };
          const withCoords = this.providers.some((provider) => Number.isFinite(Number(provider.lat)) && Number.isFinite(Number(provider.lng)));
          if (withCoords) {
            this.hasSearched = true;
            this.filtered = this.providers
              .slice()
              .sort((a, b) => distanceKm(origin, a) - distanceKm(origin, b));
            this.renderList();
            const nearest = this.filtered[0];
            if (nearest && this.map) this.map.src = mapSrc(providerQuery(nearest));
          }
          try {
            const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${origin.lat}&longitude=${origin.lng}&localityLanguage=en`;
            const response = await fetch(url);
            const data = await response.json();
            const query = [data.city, data.principalSubdivision, data.postcode].filter(Boolean).join(", ");
            if (this.input && query) this.input.value = query;
            if (!withCoords) this.search(query);
          } catch (error) {
            if (!withCoords) this.search("");
          } finally {
            this.locate.disabled = false;
          }
        },
        () => {
          this.locate.disabled = false;
        }
      );
    }

    renderList() {
      if (!this.list) return;
      this.list.replaceChildren();
      if (!this.hasSearched) {
        this.list.hidden = true;
        if (this.empty) this.empty.hidden = true;
        return;
      }
      this.list.hidden = this.filtered.length === 0;
      if (this.empty) this.empty.hidden = this.filtered.length > 0;
      this.filtered.forEach((provider) => {
        const label = document.createElement("label");
        label.className = "provider-search__result";
        const input = document.createElement("input");
        input.type = "radio";
        input.name = "provider-choice";
        input.setAttribute("data-provider-handle", provider.handle || "");
        input.checked = provider.handle === this.selectedHandle;
        const body = document.createElement("span");
        body.className = "provider-search__result-body";
        const name = document.createElement("span");
        name.className = "provider-search__result-name";
        name.textContent = provider.name || "";
        const meta = document.createElement("span");
        meta.className = "provider-search__result-meta";
        meta.textContent = [provider.address, [provider.city, provider.state, provider.zip].filter(Boolean).join(", ")]
          .filter(Boolean)
          .join(" · ");
        body.append(name);
        if (meta.textContent) body.append(meta);
        if (provider.phone) {
          const phone = document.createElement("span");
          phone.className = "provider-search__result-meta";
          phone.textContent = provider.phone;
          body.append(phone);
        }
        label.append(input, body);
        this.list.append(label);
      });
    }

    async sendProviderRequest() {
      const requested = this.requestInput?.value.trim() || "";
      const email = this.requestForm?.querySelector('[name="contact[email]"]')?.value.trim() || "";
      if (!requested || !email || !this.requestForm) {
        this.requestForm?.reportValidity?.();
        return;
      }
      if (this.requestBody) {
        this.requestBody.value = [
          "Provider add request",
          "",
          `Provider or practice: ${requested}`,
          `Customer email: ${email}`,
          "",
          "This customer could not find their provider in the PROVOQUE locator."
        ].join("\n");
      }
      if (this.requestSuccess) this.requestSuccess.hidden = true;
      this.showError("");
      this.requestSubmit?.setAttribute("aria-busy", "true");
      if (this.requestSubmit) this.requestSubmit.disabled = true;
      try {
        const response = await fetch(this.requestForm.action || `${cartRoot()}contact`, {
          method: "POST",
          body: new FormData(this.requestForm),
          headers: { Accept: "application/json, text/html" }
        });
        if (!response.ok && response.status !== 302) {
          throw new Error("request-failed");
        }
        this.mode = "request";
        this.selectedHandle = "";
        const payload = {
          choice: "not_listed",
          handle: "",
          name: "",
          accountId: "",
          requested,
          address: "",
          city: "",
          state: "",
          zip: "",
          phone: ""
        };
        writeCookie(payload);
        paintHeader(payload);
        try {
          await pushCart(payload);
        } catch (error) {}
        document.dispatchEvent(new CustomEvent(EVENT, { detail: payload }));
        if (this.requestSuccess) this.requestSuccess.hidden = false;
        this.requestPanel?.setAttribute("open", "");
      } catch (error) {
        this.showError(this.requestError);
      } finally {
        this.requestSubmit?.removeAttribute("aria-busy");
        if (this.requestSubmit) this.requestSubmit.disabled = false;
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || "";
      this.error.hidden = !message;
    }

    syncSave() {
      this.showError("");
      if (this.save) this.save.disabled = !this.consent?.checked;
    }

    async saveSelection() {
      const hasConsent = Boolean(this.consent?.checked);
      const hasProvider = Boolean(this.selectedHandle) && this.mode !== "request";
      const hasRequest = Boolean(this.requestInput?.value.trim());
      if (!hasConsent) {
        this.showError(this.errorConsent);
        this.consent?.focus();
        return;
      }
      if (!hasProvider && !hasRequest) {
        this.showError(this.errorSelect);
        this.input?.focus();
        return;
      }
      const requested = this.requestInput?.value.trim() || "";
      const provider = this.providers.find((item) => item.handle === this.selectedHandle);
      let payload;
      if (provider && this.mode !== "request") {
        payload = {
          choice: "yes",
          handle: provider.handle,
          name: provider.name,
          accountId: provider.accountId || "",
          requested: "",
          address: provider.address || "",
          city: provider.city || "",
          state: provider.state || "",
          zip: provider.zip || "",
          phone: provider.phone || ""
        };
      } else {
        payload = {
          choice: "not_listed",
          handle: "",
          name: "",
          accountId: "",
          requested,
          address: "",
          city: "",
          state: "",
          zip: "",
          phone: ""
        };
      }
      writeCookie(payload);
      paintHeader(payload);
      this.save?.setAttribute("aria-busy", "true");
      try {
        await pushCart(payload);
      } catch (error) {}
      this.save?.removeAttribute("aria-busy");
      document.dispatchEvent(new CustomEvent(EVENT, { detail: payload }));
      this.modal?.hide?.();
    }
  }

  if (!window.customElements.get("provider-search-modal")) {
    window.customElements.define("provider-search-modal", ProviderSearchModal);
  }

  window.provoqueSyncProvider = pushCart;

  document.addEventListener("click", (event) => {
    if (!event.target.closest('[aria-controls="provider-search-modal"]')) return;
    document.getElementById("sidebar-menu")?.hide?.();
  });

  document.addEventListener(EVENT, (event) => {
    if (!event.detail) return;
    writeCookie(event.detail);
    paintHeader(event.detail);
  });

  const saved = readCookie();
  paintHeader(saved);
  if (saved && (saved.choice === "yes" || saved.choice === "not_listed" || saved.choice === "no")) {
    pushCart(saved).then(() => {
      document.dispatchEvent(new CustomEvent(EVENT, { detail: saved }));
    });
  } else {
    fetch(`${cartRoot()}cart.js`, { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((current) => {
        const existingNote = current.note || "";
        const note = stripProviderNote(existingNote);
        if (note === String(existingNote || "").trim()) return null;
        return fetch(`${cartRoot()}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ note })
        }).then(() => {
          document.querySelectorAll('textarea[name="note"], input[name="note"]').forEach((field) => {
            field.value = note;
          });
        });
      })
      .catch(() => {});

  }
})();
